<h2 class="mat-h2">How the evaluation works</h2>

<p class="lead" *ngIf="season">
  Every Extraliga broadcast is graded against the criteria in the contract and
  paid accordingly. This page says what happens to a broadcast from the moment
  it reaches this app, what each criterion is worth, and who does what.
</p>
<p class="lead" *ngIf="season">
  <b>The figures below are the ones for season {{ season.season }}</b>, the one
  selected in the sidebar. They are read from that season's rate card rather
  than written into this page, so they follow the contract of whichever season
  you are looking at. The process itself has not changed between seasons; the
  rates and the list of criteria can.
</p>

<!-- 1 -->
<mat-card class="card">
  <mat-card-header>
    <mat-card-title>The journey of one broadcast</mat-card-title>
  </mat-card-header>
  <mat-card-content>
    <ol class="journey">
      <li>
        <b>The recording is analysed.</b> Before anything appears here, the
        broadcast is checked automatically against the criteria that can be
        seen or heard in the recording. The result arrives with its evidence:
        stills from the moments in question, and a minute of sound.
        <div class="muted">
          Not everything can be settled from a recording, and the analysis
          never guesses. Where it cannot tell, it says so and leaves the
          decision to a person — it will not mark a criterion as failed on a
          suspicion.
        </div>
      </li>
      <li>
        <b>The association settles what is open.</b> An admin rules on those
        undecided items, one production company at a time, and can add a note
        saying why.
      </li>
      <li>
        <b>The broadcast is released.</b> Only then does the production company
        see it. Releasing is a deliberate act on a batch — nothing is published
        automatically, because it is about money.
      </li>
      <li>
        <b>The production company answers.</b> Say whether the broadcast had an
        operated camera, then either approve the amount or send it back with a
        note saying what is wrong.
      </li>
      <li>
        <b>Approved.</b> The amount is frozen along with the rates it came from.
      </li>
    </ol>
  </mat-card-content>
</mat-card>

<!-- 1b -->
<mat-card class="card wide">
  <mat-card-header>
    <mat-card-title>The same thing as a diagram</mat-card-title>
    <mat-card-subtitle>
      Who does what, in order. Nothing moves by itself — every arrow is
      somebody pressing something.
    </mat-card-subtitle>
  </mat-card-header>
  <mat-card-content>
    <div class="diagram">
      <svg viewBox="0 0 1130 320" role="img" aria-labelledby="flow-title flow-desc">
        <title id="flow-title">The evaluation process, by who does each step</title>
        <desc id="flow-desc">
          A recording is analysed outside this app and published into it. The
          association settles what the analysis left open and releases the
          broadcast. The production company then checks the criteria, the
          evidence and the amount and declares the operated camera, and either
          approves or sends it back with a note; a note is answered by the
          association and the broadcast is released again.
        </desc>
        <defs>
          <marker id="head" viewBox="0 0 10 10" refX="9" refY="5"
                  markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z"/>
          </marker>
        </defs>

        <!-- lanes -->
        <g class="lane">
          <rect x="0" y="16" width="1130" height="80"/>
          <rect x="0" y="96" width="1130" height="120"/>
          <rect x="0" y="216" width="1130" height="96"/>
          <line x1="130" y1="16" x2="130" y2="312"/>
        </g>
        <g class="lane-label">
          <text x="14" y="52">Before</text>
          <text x="14" y="68">this app</text>
          <text x="14" y="162">The</text>
          <text x="14" y="178">association</text>
          <text x="14" y="258">The production</text>
          <text x="14" y="274">company</text>
        </g>

        <!-- edges, under the boxes so a line never sits on a label -->
        <path class="edge" marker-end="url(#head)" d="M 225 78 L 225 168 L 332 168"/>
        <text class="edge-label" x="233" y="122">published here</text>

        <path class="edge" marker-end="url(#head)" d="M 490 168 L 522 168"/>

        <path class="edge" marker-end="url(#head)" d="M 605 190 L 605 234"/>
        <text class="edge-label" x="615" y="216">now the company sees it</text>

        <path class="edge" marker-end="url(#head)" d="M 680 264 L 712 264"/>

        <path class="edge" marker-end="url(#head)" d="M 870 264 L 942 264"/>
        <text class="edge-label" x="906" y="256" text-anchor="middle">agrees</text>

        <path class="edge" marker-end="url(#head)" d="M 795 238 L 795 198"/>
        <text class="edge-label" x="806" y="212">does not agree</text>

        <path class="edge back" marker-end="url(#head)" d="M 795 142 L 795 118 L 605 118 L 605 138"/>
        <text class="edge-label" x="700" y="110" text-anchor="middle">answered, released again</text>

        <!-- boxes -->
        <g class="node plain">
          <rect x="150" y="34" width="150" height="44" rx="8"/>
          <text x="225" y="52">Recording analysed</text>
          <text class="sub" x="225" y="68">not part of this app</text>
        </g>
        <g class="node toDecide">
          <rect x="340" y="146" width="150" height="44" rx="8"/>
          <text x="415" y="164">Settle what is open</text>
          <text class="sub" x="415" y="180">To decide</text>
        </g>
        <g class="node toDecide">
          <rect x="530" y="146" width="150" height="44" rx="8"/>
          <text x="605" y="170">Release</text>
        </g>
        <g class="node toApprove">
          <rect x="530" y="242" width="150" height="44" rx="8"/>
          <text x="605" y="260">Check criteria,</text>
          <text x="605" y="276">evidence, amount</text>
        </g>
        <g class="node toApprove">
          <rect x="720" y="242" width="150" height="44" rx="8"/>
          <text x="795" y="260">Declare the</text>
          <text x="795" y="276">operated camera</text>
        </g>
        <g class="node approved">
          <rect x="950" y="242" width="150" height="44" rx="8"/>
          <text x="1025" y="260">Approve</text>
          <text class="sub" x="1025" y="276">amount frozen</text>
        </g>
        <g class="node returned">
          <rect x="720" y="146" width="150" height="44" rx="8"/>
          <text x="795" y="164">Answer the note</text>
          <text class="sub" x="795" y="180">Returned</text>
        </g>
      </svg>
    </div>
    <p class="muted">
      There is no arrow out of <b>Approve</b>: the amount is agreed, and
      reopening it is a conversation rather than a button. Up to that point the
      association can pull a broadcast back to <b>To decide</b> with
      <i>Take back for editing</i>, keeping every ruling already made.
    </p>
  </mat-card-content>
</mat-card>

<!-- 1c -->
<mat-card class="card wide" *ngIf="season">
  <mat-card-header>
    <mat-card-title>The operated camera, which runs alongside</mat-card-title>
    <mat-card-subtitle>
      The one criterion no recording can settle, so it is declared rather than
      measured — and then agreed to, or not.
    </mat-card-subtitle>
  </mat-card-header>
  <mat-card-content>
    <div class="diagram">
      <svg viewBox="0 0 900 270" role="img" aria-labelledby="cam-title cam-desc">
        <title id="cam-title">What happens to an operated-camera declaration</title>
        <desc id="cam-desc">
          The production company declares none, which is finished and needs no
          confirming, or declares an operated camera worth 825 or 1650, which
          the association confirms once it has looked. Either can be disputed
          with a reason; a dispute that is settled ends as confirmed.
        </desc>
        <defs>
          <marker id="head2" viewBox="0 0 10 10" refX="9" refY="5"
                  markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z"/>
          </marker>
        </defs>

        <path class="edge" marker-end="url(#head2)" d="M 180 116 L 215 116 L 215 56 L 242 56"/>
        <text class="edge-label" x="222" y="100">declares none</text>

        <path class="edge" marker-end="url(#head2)" d="M 180 136 L 215 136 L 215 208 L 242 208"/>
        <text class="edge-label" x="222" y="176">declares a camera</text>

        <path class="edge dashed" marker-end="url(#head2)" d="M 450 56 L 612 56"/>
        <text class="edge-label" x="531" y="40" text-anchor="middle">disputed as an</text>
        <text class="edge-label" x="531" y="50" text-anchor="middle">under-claim</text>

        <path class="edge" marker-end="url(#head2)" d="M 450 214 L 612 214"/>
        <text class="edge-label" x="531" y="206" text-anchor="middle">the association confirms</text>

        <path class="edge" marker-end="url(#head2)" d="M 450 198 L 533 198 L 533 110 L 700 110 L 700 82"/>
        <text class="edge-label" x="541" y="102">disputed, with a reason</text>

        <path class="edge" marker-end="url(#head2)" d="M 740 78 L 740 182"/>
        <text class="edge-label" x="750" y="134">settled</text>

        <g class="node plain">
          <rect x="20" y="104" width="160" height="44" rx="8"/>
          <text x="100" y="122">Not declared</text>
          <text class="sub" x="100" y="138">before approval</text>
        </g>
        <g class="node plain">
          <rect x="250" y="34" width="200" height="44" rx="8"/>
          <text x="350" y="52">None</text>
          <text class="sub" x="350" y="68">finished — nothing to confirm</text>
        </g>
        <g class="node toApprove">
          <rect x="250" y="186" width="200" height="44" rx="8"/>
          <text x="350" y="204">An operated camera</text>
          <text class="sub" x="350" y="220">{{ money(season.rates.operatedCamera / 2) }} or {{ money(season.rates.operatedCamera) }}</text>
        </g>
        <g class="node returned">
          <rect x="620" y="34" width="160" height="44" rx="8"/>
          <text x="700" y="52">Disputed</text>
          <text class="sub" x="700" y="68">reason goes to the notes</text>
        </g>
        <g class="node approved">
          <rect x="620" y="186" width="160" height="44" rx="8"/>
          <text x="700" y="210">Confirmed</text>
        </g>
      </svg>
    </div>
    <p class="muted">
      Neither answer moves money: the amount followed the declaration the
      moment the production company approved, so confirming is the record that
      somebody looked, and a dispute is the record that somebody disagrees.
      A declaration of <i>none</i> is never confirmed — it costs the production
      company the whole camera fee, so there is nothing to agree to.
    </p>
  </mat-card-content>
</mat-card>

<!-- 2 -->
<mat-card class="card">
  <mat-card-header>
    <mat-card-title>The four states</mat-card-title>
    <mat-card-subtitle>
      Shown as a chip next to every broadcast, and the quickest way to see
      whose turn it is.
    </mat-card-subtitle>
  </mat-card-header>
  <mat-card-content>
    <table class="plain">
      <thead>
        <tr>
          <th>State</th>
          <th>Production company sees it</th>
          <th>Whose turn</th>
          <th>What it means</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of states">
          <td><span class="chip state-{{ row.state }}">{{ label(row.state) }}</span></td>
          <td>{{ row.visibleToProducer ? 'yes' : 'no' }}</td>
          <td>{{ row.whoActs }}</td>
          <td>{{ row.what }}</td>
        </tr>
      </tbody>
    </table>
  </mat-card-content>
</mat-card>

<!-- 3 -->
<mat-card class="card" *ngIf="season">
  <mat-card-header>
    <mat-card-title>How the amount is worked out</mat-card-title>
  </mat-card-header>
  <mat-card-content>
    <p>
      Under this season's rates a faultless broadcast pays
      <b>{{ maximum | number }} {{ season.currency }}</b>, made up of the first
      four lines below. Promo videos beyond the mandatory set are paid on top
      of that, per video.
    </p>
    <table class="plain rates">
      <tbody>
        <tr>
          <td>Production, cut and direction</td>
          <td class="amount">{{ season.rates.production | number }}</td>
          <td class="muted">The only part the cuts come off.</td>
        </tr>
        <tr>
          <td>Own live commentary</td>
          <td class="amount">{{ season.rates.commentary | number }}</td>
          <td class="muted">Not paid where the broadcast also went out on ČT Sport+.</td>
        </tr>
        <tr>
          <td>Replay</td>
          <td class="amount">{{ season.rates.replay | number }}</td>
          <td class="muted"></td>
        </tr>
        <tr>
          <td>Operated camera on live play</td>
          <td class="amount">{{ season.rates.operatedCamera | number }}</td>
          <td class="muted">Half of it for an operated camera that was not on live play.</td>
        </tr>
        <tr class="extra">
          <td>Promo videos beyond the mandatory set</td>
          <td class="amount">{{ season.rates.extraVideo | number }}</td>
          <td class="muted">Per video, on top.</td>
        </tr>
      </tbody>
    </table>

    <p>Three rules decide what is actually paid:</p>
    <ul class="rules">
      <li>
        <b>The absolute conditions come first.</b> Two cameras, ambient sound,
        the score overlay and the whole game in the recording. If one of them
        is not met, the broadcast pays nothing — not the commentary, not the
        replay, nothing. They can also be ruled part-way: a recording that is
        whole bar one inning can be scored at, say, 0.9, which scales the
        payment down rather than wiping it out.
      </li>
      <li>
        <b>The cuts come off the production fee only.</b> Sponsor messages,
        the script and the technical parameters reduce
        {{ season.rates.production | number }} {{ season.currency }} and touch
        nothing else. All of them together can take the whole of it, but the
        commentary, the replay and the camera are unaffected.
      </li>
      <li>
        <b>Everything else is paid at its rate</b>, for what was delivered.
      </li>
    </ul>
  </mat-card-content>
</mat-card>

<!-- 4 -->
<mat-card class="card" *ngIf="criteria.length">
  <mat-card-header>
    <mat-card-title>What each criterion is worth</mat-card-title>
    <mat-card-subtitle>
      Straight from the rate card for the season in the sidebar
      ({{ season.season }}). Another season may list others.
    </mat-card-subtitle>
  </mat-card-header>
  <mat-card-content>
    <table class="plain">
      <tbody>
        <ng-container *ngFor="let row of criteria">
          <tr class="group" *ngIf="row.groupStart">
            <td colspan="2">{{ row.groupStart }}</td>
          </tr>
          <tr>
            <td class="name">{{ row.def.label }}</td>
            <td class="muted">{{ row.effect }}</td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </mat-card-content>
</mat-card>

<!-- 5 -->
<mat-card class="card">
  <mat-card-header>
    <mat-card-title>For production companies</mat-card-title>
    <mat-card-subtitle>Under <b>My broadcasts</b>.</mat-card-subtitle>
  </mat-card-header>
  <mat-card-content>
    <p>
      You see a broadcast once it has been released to you, and only your own.
      Opening one shows every criterion, what was found, the evidence for it,
      and the amount line by line.
    </p>
    <ul class="rules">
      <li>
        <b>Say whether there was an operated camera.</b> Nothing in a recording
        proves this, so it is yours to declare and it is the one thing that must
        be answered before you can approve. Declaring <i>none</i> is an answer
        like any other; it simply costs the camera fee.
      </li>
      <li>
        <b>Approve, or send it back.</b>&ngsp;<i>Something is wrong</i> asks which
        criterion it is about and what you disagree with. The broadcast goes
        back to the association, they answer in the notes and release it again.
        There is no limit on how many times.
      </li>
      <li>
        <b>Nothing changes under you once you have seen it.</b> A later
        re-analysis does not rewrite a broadcast that is with you or already
        approved; any difference is flagged for an admin to look at instead.
      </li>
      <li>
        <b>Approving is the end of it.</b> The amount is frozen with the rates
        it was worked out from. An admin can pull a broadcast back for
        correction up to that point, but not after — that is a conversation,
        not a button.
      </li>
      <li>
        <b>Notes are the place for anything else.</b> Every broadcast has a
        thread, both sides can write in it, and an admin marks a point resolved
        when it has been dealt with.
      </li>
    </ul>
  </mat-card-content>
</mat-card>

<!-- 6 -->
<mat-card class="card">
  <mat-card-header>
    <mat-card-title>For the association</mat-card-title>
    <mat-card-subtitle>Season, Criteria and Review queue.</mat-card-subtitle>
  </mat-card-header>
  <mat-card-content>
    <ul class="rules">
      <li>
        <b>Review queue</b> is the open items, grouped by production company so
        one can be cleared at a sitting. Broadcasts that already pay nothing are
        left out: ruling on them would decide nothing. The operated camera is
        not in the queue either — it is the producer's to declare.
      </li>
      <li>
        <b>Season</b> lists every broadcast with its state, its open items and
        what it would pay, and is where a batch is released. The summary at the
        top says where the work is.
      </li>
      <li>
        <b>Criteria</b> sets every criterion against every production company.
        It answers the question that comes up in a dispute: is this one company,
        or is it everybody?
      </li>
      <li>
        <b>Technical parameters are a range, not an answer.</b> Picture quality
        is a matter of degree, so this one is entered as a
        <i>production cut</i> in per cent, up to the maximum in the table above.
      </li>
      <li>
        <b>The operated camera is confirmed, or disputed.</b> A claim worth
        money is confirmed once an admin has looked; a declaration of
        <i>none</i> needs nothing, since it costs the production company the
        whole fee and nobody claims that falsely. Disputing works on any
        declaration — including a <i>none</i> that looks like an under-claim —
        and asks for a reason, which the production company reads in the notes.
        A dispute records the disagreement; it does not move money. On an
        approved broadcast the amount stands and the matter goes to invoicing;
        before that, take the broadcast back and let them declare again.
      </li>
      <li>
        <b>Take back for editing</b> returns a released broadcast to the
        association's desk, keeping every ruling already made. It is also the
        only way a fresh measurement can reach a broadcast the production
        company has already seen. It is not offered on an approved one.
      </li>
    </ul>
  </mat-card-content>
</mat-card>

<!-- 6b -->
<mat-card class="card" *ngIf="season">
  <mat-card-header>
    <mat-card-title>More than one season</mat-card-title>
  </mat-card-header>
  <mat-card-content>
    <ul class="rules">
      <li>
        <b>Every season stands on its own.</b> Its broadcasts, its rate card
        and its list of criteria are stored together, so an old evaluation is
        still read against the contract it was agreed under. A new contract
        never restates an old amount.
      </li>
      <li>
        <b>Past seasons stay open to both sides.</b> Pick the season in the
        sidebar; production companies see their own approved broadcasts there
        just as they did at the time. That is where invoicing and any later
        question about an agreed amount are answered.
      </li>
      <li>
        <b>You are told when you are not in the current one.</b> The sidebar
        says so under the picker, and the choice is remembered until you change
        it — looking something up in a past season usually takes more than one
        visit.
      </li>
    </ul>
  </mat-card-content>
</mat-card>

<!-- 7 -->
<mat-card class="card">
  <mat-card-header>
    <mat-card-title>Two things worth knowing</mat-card-title>
  </mat-card-header>
  <mat-card-content>
    <ul class="rules">
      <li>
        <b>Nobody is marked down for something nobody checked.</b> Where a
        criterion cannot be settled from the recording it is left open for a
        person, never recorded as a failure. An unanswered criterion is not a
        failed one.
      </li>
      <li>
        <b>The rules are re-applied to the whole season whenever they change.</b>
        That is why a broadcast already with a production company is frozen: a
        verdict that flipped under them a week later would be an argument, not
        a service. The difference is shown to an admin, who decides what to do
        with it.
      </li>
    </ul>
  </mat-card-content>
</mat-card>
